import { useState } from 'react'
import { Undo2 } from 'lucide-react'
import { toast } from 'sonner'

import type { ReactNode } from 'react'
import type { StockNoteRef } from '@/lib/api/stock-note-ref'
import { Button } from '@/components/ui/button'
import { m } from '@/lib/paraglide/messages'

interface NoteRollbackButtonProps {
    noteId: string
    status: string
    /** Quyền duyệt phiếu và phiếu không thuộc diện bị chặn (vd PXK gắn đơn hàng). */
    canRollback: boolean
    rolledBackBy?: StockNoteRef | null
    /** Phiếu đang xem là phiếu điều chỉnh sinh từ rollback — không rollback tiếp. */
    rollbackOf?: StockNoteRef | null
    confirmMessage: string
    pending?: boolean
    onRollback: () => Promise<unknown>
}

/**
 * Nút rollback phiếu kho lô đã duyệt (M-17), dùng chung 6 kho. Chỉ hiện với
 * phiếu approved chưa rollback và không phải phiếu điều chỉnh; lỗi nghiệp vụ
 * (thiếu tồn…) hiện đúng message BE.
 */
export function NoteRollbackButton({
    noteId,
    status,
    canRollback,
    rolledBackBy,
    rollbackOf,
    confirmMessage,
    pending,
    onRollback,
}: NoteRollbackButtonProps) {
    // Ẩn ngay khi rollback xong, không đợi chi tiết phiếu refetch về
    // `rolled_back_by` — trong khoảng đó bấm lại chỉ nhận lỗi "đã rollback".
    // Gắn theo noteId để chuyển sang phiếu khác cùng màn thì nút hiện lại.
    const [rolledBackNoteId, setRolledBackNoteId] = useState<string | null>(
        null,
    )
    if (
        status !== 'approved' ||
        !canRollback ||
        rolledBackBy ||
        rollbackOf ||
        rolledBackNoteId === noteId
    ) {
        return null
    }

    const handleClick = async () => {
        if (!window.confirm(confirmMessage)) return
        try {
            await onRollback()
            setRolledBackNoteId(noteId)
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.note_rollback_error(),
            )
        }
    }

    return (
        <Button
            type="button"
            variant="outline"
            className="h-11 md:h-9"
            onClick={handleClick}
            disabled={pending}
        >
            <Undo2 className="size-4 mr-1" aria-hidden="true" />
            {m.note_rollback_btn()}
        </Button>
    )
}

interface NoteRollbackLinksProps {
    rolledBackBy?: StockNoteRef | null
    rollbackOf?: StockNoteRef | null
    /** Link tới phiếu đối ứng — mỗi kho có route chi tiết phiếu riêng. */
    renderLink: (ref: StockNoteRef) => ReactNode
}

/** Link hai chiều giữa phiếu gốc và phiếu điều chỉnh rollback. */
export function NoteRollbackLinks({
    rolledBackBy,
    rollbackOf,
    renderLink,
}: NoteRollbackLinksProps) {
    if (!rolledBackBy && !rollbackOf) return null
    return (
        <>
            {rolledBackBy ? (
                <RollbackLinkField
                    label={m.note_rolled_back_by_label()}
                    link={renderLink(rolledBackBy)}
                />
            ) : null}
            {rollbackOf ? (
                <RollbackLinkField
                    label={m.note_rollback_of_label()}
                    link={renderLink(rollbackOf)}
                />
            ) : null}
        </>
    )
}

function RollbackLinkField({
    label,
    link,
}: {
    label: string
    link: ReactNode
}) {
    return (
        <div className="col-span-2">
            <p className="text-xs text-muted-foreground">{label}</p>
            <div className="text-base font-mono md:text-sm">{link}</div>
        </div>
    )
}
